import React, { useState } from 'react';
import { Box, Collapse, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FiCalendar } from 'react-icons/fi';
import { Campaign, Collab, isDeliveryOnlyUgc } from '@foodfluence/shared';
import { convertPostingDateToDate, formatPostingDate } from 'utils/dateUtils';
import DateSelector from '../scheduling/components/DateSelector';

interface PostingDateSelectorProps {
  scheduledPostingDate: string | null;
  onDateChange: (date: Date) => void;
  campaign?: Campaign;
  collab?: Collab;
  usePostingDateLimits?: boolean;
  onRequestExtension?: () => void;
  extensionRequested?: boolean;
}

const PostingDateSelector: React.FC<PostingDateSelectorProps> = ({
  scheduledPostingDate,
  onDateChange,
  campaign,
  collab,
  usePostingDateLimits = false,
  onRequestExtension,
  extensionRequested,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      fontFamily="display"
      color="#0E0E0E"
      bg="#FFFFFF"
      border={isOpen ? '2px solid #0E0E0E' : '1px solid #DCDCD8'}
      borderRadius="16px"
      overflow="hidden"
    >
      <HStack
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        aria-expanded={isOpen}
        minH="72px"
        px="14px"
        py="12px"
        spacing="12px"
        cursor="pointer"
        onClick={() => setIsOpen(v => !v)}
      >
        <Box
          as="span"
          w="44px"
          h="44px"
          borderRadius="12px"
          bg="#F2F2F0"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon as={FiCalendar} boxSize="20px" color="#0E0E0E" />
        </Box>
        <VStack as="span" align="start" spacing="2px" flex={1} minW={0}>
          <Text
            as="span"
            fontSize="12px"
            lineHeight="1.2"
            color="#66666A"
            fontWeight="600"
            letterSpacing="0.08em"
            textTransform="uppercase"
          >
            {isDeliveryOnlyUgc(collab, campaign) ? 'Expected Delivery Date' : 'Expected Posting Date'}
          </Text>
          <Text as="span" fontSize="16px" lineHeight="1.3" fontWeight={scheduledPostingDate ? '800' : '600'} color={scheduledPostingDate ? '#0E0E0E' : '#66666A'}>
            {scheduledPostingDate
              ? formatPostingDate(scheduledPostingDate)
              : 'Let the brand know when to expect your content'}
          </Text>
        </VStack>
        <Icon
          as={isOpen ? ChevronUpIcon : ChevronDownIcon}
          color="#0E0E0E"
          boxSize="22px"
          flexShrink={0}
        />
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <Box borderTop="1px solid #ECECEA" px="10px" pb="10px" pt="10px">
          <DateSelector
            selectedDate={scheduledPostingDate ? convertPostingDateToDate(scheduledPostingDate) : null}
            onDateChange={(date) => {
              onDateChange(date);
              setIsOpen(false);
            }}
            campaign={campaign}
            collab={collab}
            usePostingDateLimits={usePostingDateLimits}
            onRequestExtension={onRequestExtension}
            extensionRequested={extensionRequested}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default PostingDateSelector;
